<template>
    <div>
        <b-card-group deck>
            <b-card header="Claim">

                <b-card-body>
                    <b-card-text>
                        <b-row no-gutters>
                            <b-col>
                                {{ claim.billingReferenceNumber }}
                            </b-col>
                            <b-col class="text-center">
                                {{ claim.planName }}
                            </b-col>
                            <b-col
                                :class="claim.transactionStatus=='Rejected' ? 'text-right text-danger' : 'text-right'">
                                {{ claim.transactionStatus }}
                            </b-col>
                        </b-row>
                        <b-row v-if="claim.responseMessage">
                            <b-col>
                                ResponseMessage: {{ claim.responseMessage }}
                            </b-col>
                        </b-row>
                        <b-row v-if="claim.additionalMessage">
                            <b-col>
                                <strong>Additional Messages:</strong>
                                <div v-if="parsedAdditionalMessages">
                                    <div v-for="(msg, idx) in parsedAdditionalMessages.messages" :key="idx">
                                        <span v-if="msg.label">{{ msg.label }}</span>{{ msg.text }}
                                    </div>
                                </div>
                                <div v-else>
                                    {{ claim.additionalMessage }}
                                </div>
                            </b-col>
                        </b-row>
                        <b-row v-if="claim.authorizationNumber">
                            <b-col>
                                AuthorizationNumber: {{ claim.authorizationNumber }}
                            </b-col>
                        </b-row>
                    </b-card-text>

                    <b-card-text>
                        <div v-for="field in currencyFields" :key="field">
                            {{ formatField(field) }}: {{ claim[field] | currency }}
                        </div>
                    </b-card-text>
                    <b-card-text>
                        DAW: {{ claim.dawCode }}
                    </b-card-text>
                </b-card-body>

                <b-button block v-b-toggle.raw-claim size="sm">Raw</b-button>
                <b-collapse id="raw-claim">
                    <b-card-body>
                        <b-card-text>{{ claim }}</b-card-text>
                    </b-card-body>
                </b-collapse>
            </b-card>
            <b-card header="Transmission" header-class="text-center">
                <b-row>
                    <b-col>
                        <b-card-text>{{ transmission.requestString }}</b-card-text>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <b-card-text>{{ transmission.responseString }}</b-card-text>
                    </b-col>
                </b-row>

                <hr />
                <div class="text-center">Notes</div>

                <b-row v-for="note in this.notes" :key="note.id">
                    <b-col cols="auto">
                        {{ moment.utc(note.created).local().format("L LT") }} - {{ note.createdBy }}:
                    </b-col>
                    <b-col>
                        {{ note.body }}
                    </b-col>
                </b-row>

                <b-row align-v="baseline">
                    <b-col>
                        <b-input v-model="newNote.body" />
                    </b-col>
                    <b-col cols="auto">
                        <b-button @click="addNote" size="sm" variant="success">Add</b-button>
                    </b-col>
                </b-row>
            </b-card>
        </b-card-group>
    </div>
</template>

<script lang="ts">
import { Component, VModel, Vue, Watch } from "vue-property-decorator";
import { Claim } from "@/models/Claim";
import Axios from "axios";
import { EntityType } from "@/models/EntityType";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { Note, NoteVisibility } from "@/models/Note";
import moment from "moment";

@Component({
    name: "BillingDetailsComponent",
    computed: {
        moment() {
            return moment;
        },
    },
})
export default class BillingDetailsComponent extends Vue {
    @VModel({ required: true }) private claim!: Claim;

    protected transmission: any = {};
    protected notes: Note[] = [];
    protected newNote: Note = new Note();

    get currencyFields() {
        return [
            "totalPriceSubmitted",
            "adjudicated",
            "planPaid",
            "voucherPaid",
            "copay",
            "usualAndCustomary",
            "patientPay",
            "drugCost",
            "ingredientCostSubmitted",
            "dispensingFeeSubmitted",
            "incentiveSubmitted",
            "totalTaxSubmitted",
            "ingredientCostPaid",
            "dispensingFeePaid",
            "incentiveAmountPaid",
            "deliveryAmountPaid",
            "totalTaxPaid",
            "priceDifference",
            "margin",
        ];
    }

    get parsedAdditionalMessages(): any {
        if (!this.claim?.additionalMessage) return null;
        try {
            const parsed = JSON.parse(this.claim.additionalMessage);
            return parsed.messages ? parsed : null;
        } catch {
            return null; // Fallback to raw display for backward compatibility
        }
    }

    formatField(field: string): string {
        let ret = field.replace(/([a-z])([A-Z])/g, "$1 $2");
        ret = ret.substring(0, 1).toUpperCase() + ret.substring(1);
        return ret;
    }

    created() {
        console.log(`%c Created ${this.$options.name}`, "color: green");
    }

    mounted() {
        this.fetchTransmission();
        this.fetchNotes();
    }

    beforeDestroy() {
        console.log(`%c Destroying ${this.$options.name}`, "color: red");
    }

    @Watch("claim.transmissionId")
    @Watch("value.transmissionId")
    transmissionIdUpdate(_id: any, _oldId: any) {
        this.fetchTransmission();
        this.fetchNotes();
    }

    async fetchTransmission() {
        if (!this.claim?.transmissionId) return;
        try {
            const transmissionUrl = `Insurance/Transmission/${this.claim.transmissionId}`;
            const resp = await Axios.get(transmissionUrl);
            this.transmission = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async fetchNotes() {
        if (!this.claim?.id) return;
        try {
            const url = `/api/note/${this.claim.id}/${EntityType.Claim}`;
            const resp = await Axios.get<Note[]>(url);
            this.notes = resp.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async addNote() {
        try {
            this.newNote.entityType = EntityType.Claim;
            this.newNote.entityId = `${this.claim.id}`;
            this.newNote.visibility = NoteVisibility.AllUsers;

            await Axios.post("/api/Note", this.newNote);
            this.newNote = new Note();
            await this.fetchNotes();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }
}
</script>

<style scoped>
</style>
